import { ClipboardCheck, Sparkles, Trash2, ChevronRight, Zap } from "lucide-react";

/**
 * AssetDemandRow — renders a "custom job" (an /asset-demands record) as a
 * card that visually matches InspectionRow on the Swing page so the crew
 * sees custom jobs alongside their inspections. Clicking the row opens the
 * sign-off flow (AssetDemandSignoffModal).
 */
export default function AssetDemandRow({ demand, onOpen, onDelete, compact = false }) {
  const done = String(demand?.status || "").toLowerCase() === "done";
  const priority = String(demand?.priority || "normal").toLowerCase();
  const priorityTone = priority === "high"
    ? { bg: "rgba(220,38,38,0.14)", border: "rgba(220,38,38,0.35)", color: "#f87171" }
    : priority === "low"
      ? { bg: "rgba(147,197,253,0.14)", border: "rgba(147,197,253,0.35)", color: "#93c5fd" }
      : { bg: "rgba(245,130,32,0.14)", border: "rgba(245,130,32,0.35)", color: "var(--primary)" };
  return (
    <button
      onClick={onOpen}
      className="panel-heavy w-full text-left transition-transform hover:-translate-y-0.5 hover:shadow-lg"
      style={{ padding: compact ? 14 : 18 }}
      data-testid={`custom-job-row-${demand.id}`}
    >
      <div className="flex items-center gap-4">
        <div
          className="shrink-0 rounded-xl flex items-center justify-center"
          style={{
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            background: done ? "rgba(74,222,128,0.14)" : "rgba(245,130,32,0.14)",
            border: `1px solid ${done ? "rgba(74,222,128,0.35)" : "rgba(245,130,32,0.35)"}`,
            color: done ? "#4ade80" : "var(--primary)",
          }}
        >
          {done
            ? <ClipboardCheck className={compact ? "w-4 h-4" : "w-5 h-5"} />
            : <Sparkles className={compact ? "w-4 h-4" : "w-5 h-5"} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest shrink-0"
              style={{ background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
            >
              CUSTOM JOB
            </span>
            <span className="font-display font-black text-base tracking-tight text-white truncate">
              {demand.asset_id || "—"}
            </span>
            {priority !== "normal" && (
              <span
                className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest shrink-0"
                style={{ background: priorityTone.bg, border: `1px solid ${priorityTone.border}`, color: priorityTone.color }}
                title={`${priority} priority`}
              >
                {priority === "high" && <Zap className="w-2.5 h-2.5" />} {priority}
              </span>
            )}
            {done && (
              <span
                className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest shrink-0"
                style={{ background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ade80" }}
              >
                COMPLETED
              </span>
            )}
          </div>
          <div className="text-sm text-white/85 mt-1 line-clamp-2">
            {demand.text || "—"}
          </div>
          {(demand.created_by_name || demand.completed_by_name) && (
            <div className="text-[10px] uppercase tracking-widest text-muted mt-1">
              {done && demand.completed_by_name && <>Signed off by <span className="text-white">{demand.completed_by_name}</span></>}
              {!done && demand.created_by_name && <>Raised by <span className="text-white">{demand.created_by_name}</span></>}
            </div>
          )}
        </div>

        {!done && onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(demand); }}
            className="btn btn-xs btn-ghost !py-1 !px-2 shrink-0"
            title="Delete this custom job"
            data-testid={`custom-job-delete-${demand.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronRight className="w-4 h-4 text-muted shrink-0" />
      </div>
    </button>
  );
}
